import React from "react";
import Modal from "../../components/Modal.jsx";
import "../../../../components/common/waypoint/waypoint.css";

/* What is behind one of the report's figures.

   The figures are totals for a period, and Waypoint addresses one record
   by id, so a figure cannot be a Waypoint itself -- there is no "sessions
   held in March" record. It opens the rows the report already groups that
   total by, and every row IS a record: a trainer, a course, a lab, each
   opening through Waypoint. Same shape as the classroom dashboard's tiles.

   The report endpoint returns one grouping per request (trainers, course
   or labs), so a breakdown built from a grouping other than the one on
   screen is fetched when its figure is pressed -- see ReportsTab.

   A breakdown only lists what the grouping can see. Sessions in a batch
   with no trainer are held and counted, but there is no trainer row to put
   them in; the report service drops them from the per-trainer rows. Where
   the rows do not add up to the figure, the popup says how many are
   missing and why, rather than showing a list that quietly disagrees with
   the number that opened it. */

/* The figure carries the tone; there is no meter beside it. Shared with
   the Labs table on ReportsTab, which imports it from here. */
export const utilTone = (pct) => {
  if (pct >= 70) return " is-strong";
  if (pct >= 40) return " is-fair";
  return " is-weak";
};

/** Which report grouping each breakdown is built from. */
export const BREAKDOWN_VIEW = {
  sessions: "trainers",
  hours: "course",
  utilization: "labs",
  cancelled: "trainers",
  shifted: "labs",
};

const hrs = (n) => (Number(n) || 0).toFixed(1);
const sum = (rows, key) => rows.reduce((s, r) => s + (Number(r[key]) || 0), 0);

const TrainerCell = ({ row }) => (
  <div className="lp-cell">
    <span className="lp-name">{row.trainerName || "Unnamed trainer"}</span>
    {row.specialization && <span className="lp-sub">{row.specialization}</span>}
  </div>
);

const LabCell = ({ row }) => (
  <span className="lp-name">{row.classroomNo || (row.classroomId ? "Unnamed lab" : "No lab set")}</span>
);

/* Each breakdown: which rows, in what order, what columns, which record
   a row opens, and -- where the rows are a partial count of the figure --
   the sentence that says what is not in the list. */
const KINDS = {
  sessions: {
    eyebrow: "By trainer",
    noun: ["trainer", "trainers"],
    pick: (rows) => rows.filter((r) => r.sessions > 0).sort((a, b) => b.sessions - a.sessions),
    columns: ["Trainer", "Sessions", "Hours", "Batches"],
    cells: (r) => [
      { key: "who", node: <TrainerCell row={r} /> },
      { key: "sessions", node: r.sessions, num: true },
      { key: "hours", node: hrs(r.hours), num: true },
      { key: "batches", node: r.batchesCount, num: true },
    ],
    target: (r) => (r.trainerId ? ["trainer", r.trainerId] : null),
    gap: (rows, tiles) => {
      const missing = (tiles?.sessionsHeld || 0) - sum(rows, "sessions");
      return missing > 0
        ? `${missing} of the ${tiles.sessionsHeld} sessions held were in batches with no trainer set, so they have no row here.`
        : null;
    },
  },

  hours: {
    eyebrow: "By course",
    noun: ["course", "courses"],
    pick: (rows) => rows.filter((r) => r.hours > 0).sort((a, b) => b.hours - a.hours),
    columns: ["Course", "Hours", "Sessions", "Batches"],
    cells: (r) => [
      { key: "who", node: <span className="lp-name">{r.courseName || "Unnamed course"}</span> },
      { key: "hours", node: hrs(r.hours), num: true },
      { key: "sessions", node: r.sessions, num: true },
      { key: "batches", node: r.batchesCount, num: true },
    ],
    target: (r) => (r.courseId ? ["course", r.courseId] : null),
    gap: (rows, tiles) => {
      const missing = (tiles?.teachingHours || 0) - sum(rows, "hours");
      return missing >= 0.05
        ? `${hrs(missing)} of the ${hrs(tiles.teachingHours)} teaching hours were in batches with no course set, so they have no row here.`
        : null;
    },
  },

  utilization: {
    eyebrow: "By lab",
    noun: ["lab", "labs"],
    pick: (rows) => [...rows].sort((a, b) => b.utilization - a.utilization),
    columns: ["Lab", "Utilization", "Hours", "Sessions", "Status"],
    cells: (r) => [
      { key: "who", node: <LabCell row={r} /> },
      {
        key: "util",
        node: <span className={`srp-att${utilTone(r.utilization)}`}>{r.utilization.toFixed(0)}%</span>,
        num: true,
      },
      { key: "hours", node: hrs(r.hours), num: true },
      { key: "sessions", node: r.sessions, num: true },
      {
        key: "status",
        node:
          r.isActive === false ? (
            <span className="pg-pill pg-pill--bad">Out of order</span>
          ) : (
            <span className="pg-pill pg-pill--good">Active</span>
          ),
      },
    ],
    target: (r) => (r.classroomId ? ["classroom", r.classroomId] : null),
    // An average, not a sum: nothing is missing, but what it is an
    // average OF is worth saying -- empty labs pull it down.
    gap: (rows) =>
      `The average is taken across all ${rows.length} lab${rows.length === 1 ? "" : "s"}, including any with no sessions in the period.`,
  },

  cancelled: {
    eyebrow: "By trainer",
    noun: ["trainer", "trainers"],
    pick: (rows) => rows.filter((r) => r.cancelled > 0).sort((a, b) => b.cancelled - a.cancelled),
    columns: ["Trainer", "Cancelled", "Sessions held"],
    cells: (r) => [
      { key: "who", node: <TrainerCell row={r} /> },
      { key: "cancelled", node: r.cancelled, num: true },
      { key: "sessions", node: r.sessions, num: true },
    ],
    target: (r) => (r.trainerId ? ["trainer", r.trainerId] : null),
    gap: (rows, tiles) => {
      const missing = (tiles?.cancelledCount || 0) - sum(rows, "cancelled");
      return missing > 0
        ? `${missing} of the ${tiles.cancelledCount} cancellations belong to trainers who held no session in this period, or to batches with no trainer, so they cannot be listed by trainer.`
        : null;
    },
  },

  shifted: {
    eyebrow: "By lab they moved into",
    noun: ["lab", "labs"],
    pick: (rows) => rows.filter((r) => r.shiftedIn > 0).sort((a, b) => b.shiftedIn - a.shiftedIn),
    columns: ["Lab", "Shifted in", "Sessions"],
    cells: (r) => [
      { key: "who", node: <LabCell row={r} /> },
      { key: "shifted", node: r.shiftedIn, num: true },
      { key: "sessions", node: r.sessions, num: true },
    ],
    target: (r) => (r.classroomId ? ["classroom", r.classroomId] : null),
    gap: (rows, tiles) => {
      const missing = (tiles?.shiftedCount || 0) - sum(rows, "shiftedIn");
      return missing > 0
        ? `${missing} of the ${tiles.shiftedCount} shifted sessions have no lab set, so they have no row here.`
        : null;
    },
  },
};

const ReportBreakdownModal = ({ breakdown, onClose, onOpenEntity }) => {
  const { kind, label, rows, tiles, from, to, loading, error } = breakdown;
  const config = KINDS[kind];
  if (!config) return null;

  const list = config.pick(rows || []);
  const note = !loading && !error ? config.gap(rows || [], tiles) : null;
  const [one, many] = config.noun;

  let body;
  if (loading) {
    body = (
      <div className="pg-state">
        <span className="pg-spinner" role="status" aria-hidden="true" />
        <p className="pg-state-text">Loading the breakdown...</p>
      </div>
    );
  } else if (error) {
    body = <p className="pgm-empty">{error}</p>;
  } else if (!list.length) {
    body = <p className="pgm-empty">None of this can be listed {config.eyebrow.toLowerCase()}.</p>;
  } else {
    body = (
      <div className="lp-dialog-table">
        <table>
          <thead>
            <tr>
              <th>Sr. No</th>
              {config.columns.map((c) => (
                <th key={c}>{c}</th>
              ))}
              <th />
            </tr>
          </thead>
          <tbody>
            {list.map((r, i) => {
              const target = config.target(r);
              return (
                <tr key={target ? target[1] : `row-${i}`}>
                  <td className="lp-num">{i + 1}</td>
                  {config.cells(r).map((c) => (
                    <td key={c.key} className={c.num ? "lp-num" : undefined}>
                      {c.node}
                    </td>
                  ))}
                  <td className="wp-rowact">
                    {target && (
                      <button
                        type="button"
                        className="wp-link"
                        onClick={() => onOpenEntity(target[0], target[1])}
                      >
                        Open
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <Modal
      eyebrow={`${label} · ${config.eyebrow}`}
      title={label}
      sub={
        loading
          ? `${from} to ${to}`
          : `${list.length} ${list.length === 1 ? one : many} · ${from} to ${to}`
      }
      onClose={onClose}
      size="wide"
      labelledBy="cls-report-breakdown-title"
      footer={
        <button type="button" className="pg-btn pg-btn--main" onClick={onClose}>
          Close
        </button>
      }
    >
      {body}
      {note && <p className="att-note">{note}</p>}
    </Modal>
  );
};

export default ReportBreakdownModal;
